import React, { useState } from 'react';
import axios from 'axios';
import { API } from '../api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { toast } from 'sonner';
import { Key, AlertCircle, CheckCircle2, Loader2, UserPlus, Mail, Lock, User } from 'lucide-react';

const LicenseActivation = ({ onActivationSuccess }) => {
  const [step, setStep] = useState(1); // 1: license activation, 2: user registration
  const [licenseKey, setLicenseKey] = useState('');
  const [licenseData, setLicenseData] = useState(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleActivateLicense = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const response = await axios.post(`${API}/license/activate`, {
        license_key: licenseKey,
      });

      if (response.data.status === 'Active') {
        setLicenseData(response.data);
        setEmail(response.data.email || '');
        setFullName(response.data.registeredname || '');
        toast.success('Licenza verificata con successo!');
        setStep(2); // Move to registration step
      }
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Errore durante la verifica della licenza';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    // Validate passwords match
    if (password !== confirmPassword) {
      setError('Le password non coincidono');
      setLoading(false);
      return;
    }

    // Validate password length
    if (password.length < 6) {
      setError('La password deve essere di almeno 6 caratteri');
      setLoading(false);
      return;
    }

    try {
      const response = await axios.post(`${API}/auth/register`, {
        license_key: licenseKey,
        email: email,
        password: password,
        full_name: fullName,
      });

      if (response.data.success) {
        toast.success('Registrazione completata! Accedi ora con le tue credenziali.');
        if (onActivationSuccess) {
          onActivationSuccess(response.data.user);
        }
      }
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Errore durante la registrazione';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const formatLicenseKey = (value) => {
    // Accept any alphanumeric characters and hyphens, convert to lowercase to match WHMCS format
    const cleaned = value.replace(/[^A-Za-z0-9-]/g, '').toLowerCase();
    return cleaned;
  };

  const handleLicenseKeyChange = (e) => {
    const formatted = formatLicenseKey(e.target.value);
    setLicenseKey(formatted);
  };

  // Check if license key is valid (at least 10 alphanumeric characters)
  const isLicenseKeyValid = () => {
    const cleaned = licenseKey.replace(/[^A-Za-z0-9]/g, '');
    return cleaned.length >= 10;
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC] p-4">
      <div className="w-full max-w-md">
        {/* Logo and Header */}
        <div className="text-center mb-8">
          <img
            src="https://maxvalle.it/wp-content/uploads/2026/01/verticale-arancio-scuro-2.png"
            alt="Logo"
            className="h-20 mx-auto mb-4"
            data-testid="license-logo"
          />
          <h1 className="text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope' }}>
            {step === 1 ? 'Attivazione Licenza' : 'Crea il tuo Account'}
          </h1>
          <p className="text-slate-600">
            {step === 1 
              ? 'Inserisci la chiave di licenza per iniziare'
              : 'Completa la registrazione per accedere'}
          </p>
        </div>

        {/* Step Indicator */}
        <div className="flex items-center justify-center mb-6">
          <div className="flex items-center">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
              step >= 1 ? 'bg-[#D94E1F] text-white' : 'bg-slate-200 text-slate-600'
            }`}>
              1
            </div>
            <div className={`w-16 h-1 ${
              step >= 2 ? 'bg-[#D94E1F]' : 'bg-slate-200'
            }`} />
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
              step >= 2 ? 'bg-[#D94E1F] text-white' : 'bg-slate-200 text-slate-600'
            }`}>
              2
            </div>
          </div>
        </div>

        {/* Card */}
        <div className="bg-white rounded-xl border border-slate-100 shadow-[0_2px_8px_rgba(0,0,0,0.04)] p-8">
          {/* Step 1: License Activation */}
          {step === 1 && (
            <form onSubmit={handleActivateLicense} className="space-y-6" data-testid="license-activation-form">
              <div>
                <Label htmlFor="license-key" className="text-slate-700 font-medium flex items-center gap-2">
                  <Key className="w-4 h-4 text-[#D94E1F]" />
                  Chiave di Licenza
                </Label>
                <Input
                  id="license-key"
                  type="text"
                  value={licenseKey}
                  onChange={handleLicenseKeyChange}
                  placeholder="mxv-xxxxxxxxxx"
                  className="mt-2 font-mono text-center text-lg tracking-wider rounded-lg border-slate-200 focus:ring-2 focus:ring-[#D94E1F]/20 focus:border-[#D94E1F]"
                  required
                  data-testid="license-key-input"
                />
                <div className="flex items-center justify-between mt-2">
                  <p className="text-xs text-slate-500">
                    Inserisci la chiave esattamente come fornita da WHMCS
                  </p>
                  {licenseKey && (
                    <p className={`text-xs font-medium ${
                      isLicenseKeyValid() ? 'text-green-600' : 'text-slate-400'
                    }`}>
                      {licenseKey.replace(/[^A-Za-z0-9]/g, '').length} caratteri
                    </p>
                  )}
                </div>
              </div>

              {error && (
                <Alert variant="destructive" className="bg-red-50 border-red-200" data-testid="license-error">
                  <AlertCircle className="h-4 w-4 text-red-600" />
                  <AlertDescription className="text-red-800">{error}</AlertDescription>
                </Alert>
              )}

              <Button
                type="submit"
                disabled={loading || !isLicenseKeyValid()}
                className="w-full bg-[#D94E1F] hover:bg-[#B83E15] text-white rounded-full px-6 py-3 font-medium"
                data-testid="activate-button"
              >
                {loading ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Verifica in corso...</>
                ) : (
                  <><CheckCircle2 className="w-4 h-4 mr-2" />Verifica Licenza</>
                )}
              </Button>
            </form>
          )}

          {/* Step 2: User Registration */}
          {step === 2 && (
            <form onSubmit={handleRegister} className="space-y-6" data-testid="registration-form">
              <div>
                <Label htmlFor="email" className="text-slate-700 font-medium flex items-center gap-2">
                  <Mail className="w-4 h-4 text-[#D94E1F]" />
                  Email (da WHMCS)
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  className="mt-2 rounded-lg border-slate-200 bg-slate-50"
                  readOnly
                  data-testid="email-input"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Questa è l'email associata alla tua licenza
                </p>
              </div>

              <div>
                <Label htmlFor="full-name" className="text-slate-700 font-medium flex items-center gap-2">
                  <User className="w-4 h-4 text-[#D94E1F]" />
                  Nome Completo
                </Label>
                <Input
                  id="full-name"
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  className="mt-2 rounded-lg border-slate-200 focus:ring-2 focus:ring-[#D94E1F]/20 focus:border-[#D94E1F]"
                  required
                  data-testid="full-name-input"
                />
              </div>

              <div>
                <Label htmlFor="password" className="text-slate-700 font-medium flex items-center gap-2">
                  <Lock className="w-4 h-4 text-[#D94E1F]" />
                  Password
                </Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Minimo 6 caratteri"
                  className="mt-2 rounded-lg border-slate-200 focus:ring-2 focus:ring-[#D94E1F]/20 focus:border-[#D94E1F]"
                  required
                  minLength={6}
                  data-testid="password-input"
                />
              </div>

              <div>
                <Label htmlFor="confirm-password" className="text-slate-700 font-medium flex items-center gap-2">
                  <Lock className="w-4 h-4 text-[#D94E1F]" />
                  Conferma Password
                </Label>
                <Input
                  id="confirm-password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Ripeti la password"
                  className="mt-2 rounded-lg border-slate-200 focus:ring-2 focus:ring-[#D94E1F]/20 focus:border-[#D94E1F]"
                  required
                  data-testid="confirm-password-input"
                />
              </div>

              {error && (
                <Alert variant="destructive" className="bg-red-50 border-red-200">
                  <AlertCircle className="h-4 w-4 text-red-600" />
                  <AlertDescription className="text-red-800">{error}</AlertDescription>
                </Alert>
              )}

              <div className="flex gap-3">
                <Button
                  type="button"
                  onClick={() => setStep(1)}
                  variant="outline"
                  className="flex-1"
                  data-testid="back-button"
                >
                  Indietro
                </Button>
                <Button
                  type="submit"
                  disabled={loading}
                  className="flex-1 bg-[#D94E1F] hover:bg-[#B83E15] text-white"
                  data-testid="register-button"
                >
                  {loading ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Registrazione...</>
                  ) : (
                    <><UserPlus className="w-4 h-4 mr-2" />Registrati</>
                  )}
                </Button>
              </div>
            </form>
          )}

          {/* Help Text */}
          <div className="mt-6 pt-6 border-t border-slate-100">
            <p className="text-sm text-slate-600 text-center">
              Non hai ancora una licenza?{' '}
              <a
                href="https://clienti.maxvalle.it/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#D94E1F] hover:underline font-medium"
              >
                Acquista ora
              </a>
            </p>
            <p className="text-xs text-slate-500 text-center mt-2">
              Hai bisogno di aiuto?{' '}
              <a href="mailto:support@maxvalle.it" className="text-[#D94E1F] hover:underline">
                Contatta il supporto
              </a>
            </p>
          </div>
        </div>

        {/* Info Box */}
        {step === 1 && (
          <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-900">
                <p className="font-medium mb-1">Informazioni sulla Licenza</p>
                <ul className="list-disc list-inside space-y-1 text-blue-800">
                  <li>Licenza personale legata alla tua email</li>
                  <li>Verifica automatica ogni 24 ore</li>
                  <li>Funzionamento offline fino a 24 ore</li>
                </ul>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LicenseActivation;
